import * as Dialog from '@radix-ui/react-dialog';
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FormError, Label } from '@/components/ui/field';
import { RequiredFieldsNote } from '@/components/ui/form-section';
import { useCreateClient, useSetClientActive, useUpdateClient } from '@/hooks/clients';
import { ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast-context';
import type { ClientDetail, ClientView } from '@shared/schemas/client';

/**
 * Create or edit a client.
 *
 * No address fields, and that is the point. An address belongs to a property
 * (P14 — the prototype has a client-level one and property-level ones that
 * disagree), so it is added on the client's detail page rather than here.
 */
export function ClientFormDialog({
  open,
  onOpenChange,
  client,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client?: ClientView;
  onCreated?: (created: ClientDetail) => void;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          <ClientForm
            client={client}
            onCreated={onCreated}
            onDone={() => {
              onOpenChange(false);
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ClientForm({
  client,
  onDone,
  onCreated,
}: {
  client?: ClientView;
  onDone: () => void;
  onCreated?: (created: ClientDetail) => void;
}) {
  const toast = useToast();
  const isEdit = client !== undefined;

  const [firstName, setFirstName] = useState(client?.firstName ?? '');
  const [lastName, setLastName] = useState(client?.lastName ?? '');
  const [businessName, setBusinessName] = useState(client?.businessName ?? '');
  const [email, setEmail] = useState(client?.email ?? '');
  const [phone, setPhone] = useState(client?.phone ?? '');
  const [isHighPriority, setIsHighPriority] = useState(client?.isHighPriority ?? false);
  const [customerSince, setCustomerSince] = useState(client?.customerSince ?? '');
  const [satisfaction, setSatisfaction] = useState(
    client?.satisfactionRating === null || client?.satisfactionRating === undefined
      ? ''
      : String(client.satisfactionRating),
  );

  const create = useCreateClient();
  const update = useUpdateClient(client?.id ?? '');
  const setActive = useSetClientActive(client?.id ?? '');
  const mutation = isEdit ? update : create;

  const error = mutation.error instanceof ApiError ? mutation.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  function submit(event: FormEvent) {
    event.preventDefault();

    mutation.mutate(
      {
        firstName,
        lastName,
        businessName,
        email,
        phone,
        isHighPriority,
        satisfactionRating: satisfaction === '' ? null : Number(satisfaction),
        // Empty means "let the server decide", which on create is today. That
        // is the P10 fix — the field is never left with nothing in it.
        customerSince: customerSince === '' ? null : customerSince,
      },
      {
        onSuccess: (saved) => {
          toast.success(isEdit ? `Saved ${saved.displayName}.` : `Added ${saved.displayName}.`);
          if (!isEdit) onCreated?.(saved);
          onDone();
        },
      },
    );
  }

  return (
    <>
      <Dialog.Title className="text-h2 text-ink-900">
        {isEdit ? `Edit ${client.displayName}` : 'Add a client'}
      </Dialog.Title>
      <Dialog.Description className="mt-1 text-body text-ink-500">
        Their addresses live on their properties, which you add next.
      </Dialog.Description>

      <form onSubmit={submit} className="mt-5 space-y-4">
        <RequiredFieldsNote />

        {error && Object.keys(fieldErrors).length === 0 ? (
          <FormError>{error.message}</FormError>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="First name"
            name="firstName"
            required
            value={firstName}
            error={fieldErrors.firstName}
            onChange={(event) => {
              setFirstName(event.target.value);
            }}
          />
          <Field
            label="Last name"
            name="lastName"
            required
            value={lastName}
            error={fieldErrors.lastName}
            onChange={(event) => {
              setLastName(event.target.value);
            }}
          />
        </div>

        <Field
          label="Business name"
          name="businessName"
          value={businessName}
          error={fieldErrors.businessName}
          onChange={(event) => {
            setBusinessName(event.target.value);
          }}
          hint="For a company or a property manager. When it is set it leads on every screen, with the person above shown as the contact."
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Email"
            name="email"
            type="email"
            value={email}
            error={fieldErrors.email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
          <Field
            label="Phone"
            name="phone"
            type="tel"
            value={phone}
            error={fieldErrors.phone}
            onChange={(event) => {
              setPhone(event.target.value);
            }}
          />
        </div>
        <p className="text-meta text-ink-500">One of those two is enough, but not neither.</p>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Customer since"
            name="customerSince"
            type="date"
            value={customerSince}
            error={fieldErrors.customerSince}
            onChange={(event) => {
              setCustomerSince(event.target.value);
            }}
            hint={isEdit ? undefined : 'Leave empty and today is used.'}
          />

          <div className="space-y-1.5">
            <Label htmlFor="client-satisfaction">Satisfaction</Label>
            <select
              id="client-satisfaction"
              name="satisfactionRating"
              value={satisfaction}
              onChange={(event) => {
                setSatisfaction(event.target.value);
              }}
              className="min-h-control w-full rounded-sm border border-ink-300 bg-white px-3 text-body"
            >
              <option value="">Not rated</option>
              {[1, 2, 3, 4, 5].map((value) => (
                <option key={value} value={value}>
                  {value} of 5
                </option>
              ))}
            </select>
            {/* Open question 8, answered: internal. Saying so on the control
                itself is what stops it drifting into a client-facing screen. */}
            <p className="text-meta text-ink-500">Internal only — the client never sees this.</p>
          </div>
        </div>

        <div className="rounded-sm border border-ink-300 bg-ink-100/40 p-3">
          <label className="flex min-h-touch items-center gap-2.5">
            <input
              type="checkbox"
              name="isHighPriority"
              checked={isHighPriority}
              onChange={(event) => {
                setIsHighPriority(event.target.checked);
              }}
              className="size-4 shrink-0 accent-honey-500"
            />
            <span className="text-label text-ink-900">High priority</span>
          </label>
          <p className="mt-1 text-meta text-ink-500">
            Shows a star on every list, and is what makes calendar sync available to them — the
            short-term-rental workflow is scoped to high-priority clients.
          </p>
        </div>

        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
          {isEdit ? (
            <Button
              variant="outline"
              className="sm:mr-auto"
              disabled={setActive.isPending}
              onClick={() => {
                setActive.mutate(!client.isActive, {
                  onSuccess: (saved) => {
                    toast.success(
                      saved.isActive
                        ? `${saved.displayName} is active again.`
                        : `${saved.displayName} archived.`,
                    );
                    onDone();
                  },
                  onError: (cause) => {
                    toast.error(
                      cause instanceof ApiError ? cause.message : 'That could not be saved.',
                    );
                  },
                });
              }}
            >
              {client.isActive ? 'Archive this client' : 'Bring them back'}
            </Button>
          ) : null}

          <Dialog.Close asChild>
            <Button variant="outline" disabled={mutation.isPending}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add client'}
          </Button>
        </div>
      </form>
    </>
  );
}
